import React, { useEffect, useState } from "react";
import { ArrowUp, Loader2 } from "lucide-react";
import ModelSelect from "./ModelSelect";
import { AttachButton, AttachmentTray } from "./compose/Attachments";
import { AppTypeChips, APP_TYPES } from "./compose/AppTypeChips";
import BuildSettings from "./compose/BuildSettings";
import MicButton from "./compose/MicButton";
import { useAttachments } from "../hooks/useAttachments";
import { useVoiceInput } from "../hooks/useVoiceInput";
import { useCredits } from "../context/CreditsContext";

export const DEFAULT_MODEL = "auto";

export const PromptBox = ({ onSubmit, loading, testidPrefix = "hero", placeholder, allowAttach = true, prefill }) => {
  const [text, setText] = useState("");
  const [type, setType] = useState("web");
  const [model, setModel] = useState(DEFAULT_MODEL);
  const [plan, setPlan] = useState(true);
  const [ai, setAi] = useState(false);
  const att = useAttachments();
  const voice = useVoiceInput((t) => setText((cur) => (cur ? `${cur.trimEnd()} ${t}` : t)));
  const { costOf } = useCredits();
  const isIdea = type === "idea";
  const usePlan = plan || isIdea;
  const cost = costOf(model, usePlan, ai);
  const ready = text.trim().length >= 3 && !loading && !att.uploading;

  useEffect(() => { if (prefill?.text) setText(prefill.text); }, [prefill]);

  const submit = (e) => {
    e?.preventDefault();
    if (!ready) return;
    voice.stop();
    onSubmit(text.trim(), model, { mode: usePlan ? "plan" : "build", ai_images: ai && !usePlan, attachments: att.ids, app_type: type });
  };

  return (
    <div className="w-full space-y-4">
      <AppTypeChips value={type} onChange={setType} testid={`${testidPrefix}-app-type`} />
      <form onSubmit={submit}
        onDragOver={(e) => allowAttach && e.preventDefault()}
        onDrop={(e) => { if (allowAttach) { e.preventDefault(); att.add(e.dataTransfer.files); } }}
        className="glow-ring rounded-[28px] bg-white p-4 sm:p-5 text-left" data-testid={`${testidPrefix}-prompt-card`}>
        <textarea value={text} onChange={(e) => setText(e.target.value)} onPaste={allowAttach ? att.onPaste : undefined}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) submit(e); }}
          placeholder={placeholder || APP_TYPES.find((t) => t.id === type).placeholder} rows={3} data-testid={`${testidPrefix}-prompt-input`}
          className="w-full resize-none bg-transparent px-1.5 text-base sm:text-lg text-slate-800 placeholder:text-slate-400 outline-none leading-relaxed min-h-[88px]" />
        <AttachmentTray items={att.items} onRemove={att.remove} testid={`${testidPrefix}-attachment`} />
        <div className="flex items-center justify-between gap-2 pt-2">
          <div className="flex items-center gap-1.5 min-w-0">
            {allowAttach && <AttachButton onFiles={att.add} disabled={att.items.length >= 4} testid={`${testidPrefix}-attach-button`} />}
            <ModelSelect value={model} onChange={setModel} testid={`${testidPrefix}-model-selector`} variant="ghost" />
          </div>
          <div className="flex items-center gap-1 sm:gap-1.5">
            {cost != null && <span className="whitespace-nowrap text-[11px] sm:text-xs font-mono text-slate-500 mr-1" data-testid={`${testidPrefix}-cost`}>{usePlan ? <span className="hidden sm:inline">plan · </span> : ""}{cost} cr</span>}
            <BuildSettings plan={usePlan} setPlan={setPlan} ai={ai} setAi={setAi} planLocked={isIdea} testid={`${testidPrefix}-settings`} />
            <MicButton voice={voice} testid={`${testidPrefix}-mic-button`} />
            <button type="submit" disabled={!ready} data-testid={`${testidPrefix}-generate-button`} aria-label={usePlan ? "Plan it" : "Build it"} title={usePlan ? "Plan it" : "Build it"}
              className="w-11 h-11 sm:w-auto sm:px-4 rounded-full flex items-center justify-center gap-1.5 bg-slate-900 text-white text-sm font-semibold hover:bg-slate-700 disabled:bg-slate-100 disabled:text-slate-400 transition-colors">
              {loading ? <Loader2 size={17} className="animate-spin" /> : <ArrowUp size={18} />}
              <span className="hidden sm:inline">{usePlan ? "Plan it" : "Build it"}</span>
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default PromptBox;
